import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useApiQuery } from "../hooks/use-api-query";
import type {
  SuggestedStory,
  Test,
  UserFlowPriority,
  UserStoriesRegistry,
  UserStory,
  UserStoryStatus,
} from "../types";
import { humanizeText } from "../utils/text";

interface UserStoriesPanelProps {
  appConfigId: string;
}

const getStatusColor = (status: UserStoryStatus) => {
  switch (status) {
    case "covered":
      return "success";
    case "partial":
      return "warning";
    case "uncovered":
      return "error";
    case "broken":
      return "error";
    default:
      return "default";
  }
};

const getPriorityColor = (priority: UserFlowPriority) => {
  switch (priority) {
    case "urgent":
      return "error";
    case "high":
      return "warning";
    case "medium":
      return "info";
    case "low":
      return "default";
    default:
      return "default";
  }
};

interface UserStoryCardProps {
  story: UserStory;
  testMap: Map<string, Test>;
}

function UserStoryCard({ story, testMap }: UserStoryCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <CardContent sx={{ pb: expanded ? 2 : 1 }}>
        {/* Header: Title + Chips */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {story.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {story.description}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={humanizeText(story.status)}
              color={getStatusColor(story.status)}
              size="small"
            />
            <Chip
              label={humanizeText(story.priority)}
              color={getPriorityColor(story.priority)}
              size="small"
              variant="outlined"
            />
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        </Stack>

        {/* Expanded Details */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />

          {/* Coverage info */}
          {story.covered_by.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                Covered by ({story.covered_by.length})
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                {story.covered_by.map((testId) => (
                  <Chip
                    key={testId}
                    label={testMap.get(testId)?.name ?? testId.slice(0, 8)}
                    size="small"
                    component={Link}
                    to={`/tests/${testId}`}
                    clickable
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Gaps */}
          {story.gaps.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="warning.main" fontWeight="medium">
                Gaps ({story.gaps.length})
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {story.gaps.map((gap, i) => (
                  <Typography key={i} variant="body2">
                    • {gap}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {/* Insights (timeline) */}
          {story.insights.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                Insights ({story.insights.length})
              </Typography>
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {story.insights.map((insight, i) => (
                  <Box key={i}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(insight.ts).toLocaleString()} · {insight.by}
                    </Typography>
                    <Typography variant="body2">{insight.note}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

function SuggestedStoryCard({ story }: { story: SuggestedStory }) {
  return (
    <Card variant="outlined" sx={{ mb: 1, borderStyle: "dashed" }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {story.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {story.description}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Discovered {new Date(story.discovered_at).toLocaleDateString()}
            </Typography>
          </Box>
          <Chip
            label={humanizeText(story.priority)}
            color={getPriorityColor(story.priority)}
            size="small"
            variant="outlined"
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

export function UserStoriesPanel({ appConfigId }: UserStoriesPanelProps) {
  const [tab, setTab] = useState<"confirmed" | "suggested">("confirmed");

  const { data: kbs, isLoading } = useApiQuery<UserStoriesRegistry[]>(
    `/user-stories-kb?app_config_id=${appConfigId}`,
    { enabled: !!appConfigId },
  );

  // Extract stories and suggested from first registry (one per app config)
  const registry = kbs?.[0];
  const stories = registry?.stories ?? [];
  const suggested = registry?.suggested ?? [];

  // Collect all test IDs from stories
  const allTestIds = useMemo(() => {
    const ids = new Set<string>();
    for (const story of stories) {
      for (const testId of story.covered_by) {
        ids.add(testId);
      }
    }
    return Array.from(ids);
  }, [stories]);

  // Fetch tests by IDs
  const { data: tests } = useApiQuery<Test[]>(`/tests?ids=${allTestIds.join(",")}`, {
    enabled: allTestIds.length > 0,
  });

  // Create lookup map
  const testMap = useMemo(() => {
    const map = new Map<string, Test>();
    if (tests) {
      for (const test of tests) {
        map.set(test.id, test);
      }
    }
    return map;
  }, [tests]);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!registry) {
    return (
      <Typography color="text.secondary" align="center" py={4}>
        No user stories found for this app configuration.
      </Typography>
    );
  }

  return (
    <Box>
      <TabContext value={tab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <TabList onChange={(_, v) => setTab(v)}>
            <Tab label={`Confirmed (${stories.length})`} value="confirmed" />
            <Tab label={`Suggested (${suggested.length})`} value="suggested" />
          </TabList>

          {/* Registry Metadata */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ color: "text.secondary", pr: 1 }}
            divider={<Typography variant="caption">·</Typography>}
          >
            <Typography variant="caption">v{registry.version}</Typography>
            <Typography variant="caption">
              Created {new Date(registry.created_at).toLocaleDateString()}
            </Typography>
            {registry.updated_at && (
              <Typography variant="caption">
                Updated {new Date(registry.updated_at).toLocaleDateString()}
              </Typography>
            )}
          </Stack>
        </Box>

        <TabPanel value="confirmed" sx={{ px: 0 }}>
          {stories.length === 0 ? (
            <Typography color="text.secondary" align="center" py={4}>
              No confirmed stories yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {stories.map((story) => (
                <UserStoryCard key={story.id} story={story} testMap={testMap} />
              ))}
            </Stack>
          )}
        </TabPanel>

        <TabPanel value="suggested" sx={{ px: 0 }}>
          {suggested.length === 0 ? (
            <Typography color="text.secondary" align="center" py={4}>
              No suggested stories pending review.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {suggested.map((story) => (
                <SuggestedStoryCard key={story.id} story={story} />
              ))}
            </Stack>
          )}
        </TabPanel>
      </TabContext>
    </Box>
  );
}
